import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { emailTemplates, type TemplateType, type EmailTemplateRow } from '@/api/emailTemplates';
import { PageHeader } from '@/components/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';

interface TemplateConfig {
  type: TemplateType;
  label: string;
  description: string;
  variables: string[];
}

const TEMPLATES: TemplateConfig[] = [
  {
    type: 'invite',
    label: 'Invite email',
    description: 'Sent when a new user is invited to the portal.',
    variables: ['{{recipientName}}', '{{inviterName}}', '{{resellerName}}', '{{acceptUrl}}', '{{expiresAt}}'],
  },
  {
    type: 'password_reset',
    label: 'Password reset',
    description: 'Sent when a user requests a password reset.',
    variables: ['{{recipientName}}', '{{resetUrl}}', '{{expiresAt}}'],
  },
  {
    type: 'welcome',
    label: 'Welcome email',
    description: 'Sent after a user activates their account.',
    variables: ['{{recipientName}}', '{{resellerName}}'],
  },
  {
    type: 'notification',
    label: 'Notification',
    description: 'General notification email template.',
    variables: ['{{recipientName}}', '{{message}}', '{{resellerName}}'],
  },
];

interface TemplateCardProps {
  config: TemplateConfig;
  row: EmailTemplateRow | undefined;
}

function TemplateCard({ config, row }: TemplateCardProps) {
  const qc = useQueryClient();
  const [subject, setSubject] = useState(row?.subject ?? '');
  const [htmlBody, setHtmlBody] = useState(row?.html_body ?? '');
  const [editing, setEditing] = useState(false);
  const [showDefault, setShowDefault] = useState(false);

  // Fetch the built-in default template so we can show it alongside the
  // override. Enabled only when the user expands the "view default"
  // section to avoid loading 4 default templates on every page mount.
  const { data: defaultRow, isLoading: defaultLoading } = useQuery({
    queryKey: ['email-templates', config.type, 'default'],
    queryFn: () => emailTemplates.getDefault(config.type),
    enabled: showDefault,
    staleTime: 5 * 60_000, // built-in defaults rarely change
  });

  // Keep local state in sync when the query re-fetches (e.g. after save/delete).
  // We only sync when not actively editing so we don't stomp user input.
  // Deriving from the prop here directly avoids stale closures.
  const rowSubject = row?.subject ?? '';
  const rowHtmlBody = row?.html_body ?? '';

  function startEdit() {
    setSubject(rowSubject);
    setHtmlBody(rowHtmlBody);
    setEditing(true);
  }

  function cancelEdit() {
    setSubject(rowSubject);
    setHtmlBody(rowHtmlBody);
    setEditing(false);
  }

  const save = useMutation({
    mutationFn: () => emailTemplates.save(config.type, { subject, html_body: htmlBody }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['email-templates'] });
      setEditing(false);
    },
  });

  const remove = useMutation({
    mutationFn: () => emailTemplates.remove(config.type),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['email-templates'] });
      setSubject('');
      setHtmlBody('');
      setEditing(false);
    },
  });

  const isOverride = row?.is_override ?? false;

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3">
          <div className="space-y-0.5">
            <CardTitle className="text-base">{config.label}</CardTitle>
            <p className="text-xs text-muted-foreground">{config.description}</p>
          </div>
          <Badge variant={isOverride ? 'default' : 'secondary'} className="shrink-0 text-xs">
            {isOverride ? 'Custom override' : 'Default'}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Variable chips */}
        <div className="space-y-1.5">
          <p className="text-xs font-medium text-muted-foreground">Available variables</p>
          <div className="flex flex-wrap gap-1.5">
            {config.variables.map((v) => (
              <code
                key={v}
                className="rounded bg-secondary px-1.5 py-0.5 text-xs font-mono text-foreground select-all"
              >
                {v}
              </code>
            ))}
          </div>
        </div>

        {editing ? (
          <div className="space-y-3 border-t border-border pt-4">
            <div className="space-y-1.5">
              <Label htmlFor={`subject-${config.type}`}>Subject</Label>
              <Input
                id={`subject-${config.type}`}
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Email subject line"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor={`body-${config.type}`}>HTML body</Label>
              <textarea
                id={`body-${config.type}`}
                value={htmlBody}
                onChange={(e) => setHtmlBody(e.target.value)}
                placeholder="<p>Hello {{recipientName}},</p>"
                rows={10}
                className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-mono ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 resize-y"
              />
            </div>
            {save.isError && (
              <p role="alert" className="text-xs text-destructive">
                {save.error instanceof Error ? save.error.message : 'Save failed.'}
              </p>
            )}
            <div className="flex flex-wrap gap-2">
              <Button
                size="sm"
                disabled={save.isPending || !subject.trim() || !htmlBody.trim()}
                onClick={() => save.mutate()}
              >
                {save.isPending ? 'Saving…' : 'Save override'}
              </Button>
              <Button size="sm" variant="outline" disabled={save.isPending} onClick={cancelEdit}>
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <div className="border-t border-border pt-4 space-y-3">
            {isOverride ? (
              <div className="space-y-2">
                <div className="space-y-0.5">
                  <p className="text-xs text-muted-foreground">Override subject</p>
                  <p className="text-sm">{row!.subject}</p>
                </div>
                <div className="space-y-0.5">
                  <p className="text-xs text-muted-foreground">Override HTML body</p>
                  <pre className="max-h-32 overflow-auto rounded bg-secondary p-2 text-xs font-mono whitespace-pre-wrap break-all">
                    {row!.html_body}
                  </pre>
                </div>
              </div>
            ) : (
              <p className="text-xs text-muted-foreground italic">
                No override set — using the built-in default below.
              </p>
            )}

            {/* Always-available "show default" section. Per Sam (2026-05-14)
                operators want to see what they're overriding (or what the
                system will send if they don't override) — fetches the
                on-disk template via GET /api/email-templates/:type/default
                only when expanded so we don't load 4 defaults per page. */}
            <div className="border-t border-dashed border-border pt-3 space-y-2">
              <button
                type="button"
                onClick={() => setShowDefault((v) => !v)}
                className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
              >
                {showDefault ? 'Hide built-in default' : 'Show built-in default'}
              </button>
              {showDefault && (
                <>
                  {defaultLoading && <p className="text-xs text-muted-foreground">Loading default…</p>}
                  {!defaultLoading && defaultRow && !defaultRow.has_default && (
                    <p className="text-xs text-muted-foreground italic">
                      No built-in default for this template — it's override-only.
                    </p>
                  )}
                  {!defaultLoading && defaultRow?.has_default && (
                    <div className="space-y-2">
                      <div className="space-y-0.5">
                        <p className="text-xs text-muted-foreground">Default subject</p>
                        <p className="text-sm font-mono">{defaultRow.subject ?? '—'}</p>
                      </div>
                      <div className="space-y-0.5">
                        <p className="text-xs text-muted-foreground">Default HTML body</p>
                        <pre className="max-h-64 overflow-auto rounded bg-slate-50 dark:bg-slate-900 p-2 text-xs font-mono whitespace-pre-wrap break-all border border-border">
                          {defaultRow.html_body ?? '—'}
                        </pre>
                        <button
                          type="button"
                          onClick={() => {
                            setSubject(defaultRow.subject ?? '');
                            setHtmlBody(defaultRow.html_body ?? '');
                            setEditing(true);
                          }}
                          className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                        >
                          Use this as starting point for override
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant="outline" onClick={startEdit}>
                {isOverride ? 'Edit override' : 'Create override'}
              </Button>
              {isOverride && (
                <Button
                  size="sm"
                  variant="outline"
                  className="text-destructive hover:text-destructive"
                  disabled={remove.isPending}
                  onClick={() => {
                    if (window.confirm('Remove this override and revert to the system default?')) {
                      remove.mutate();
                    }
                  }}
                >
                  {remove.isPending ? 'Removing…' : 'Delete override'}
                </Button>
              )}
            </div>
            {remove.isError && (
              <p role="alert" className="text-xs text-destructive">
                {remove.error instanceof Error ? remove.error.message : 'Remove failed.'}
              </p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export function EmailTemplatesPage() {
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: ['email-templates'],
    queryFn: () => emailTemplates.list(),
  });

  const rowMap = new Map<TemplateType, EmailTemplateRow>(
    (data?.templates ?? []).map((t) => [t.type, t]),
  );

  return (
    <>
      <PageHeader
        eyebrow="Settings"
        title="Email templates"
        description="Override the default email templates sent by the system. Variables in {{double braces}} are replaced automatically."
      />

      {isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
      {error && (
        <p role="alert" className="text-sm text-destructive">
          {error instanceof Error ? error.message : 'Failed to load templates.'}
        </p>
      )}

      <div className="grid gap-6 max-w-3xl">
        {!isLoading && TEMPLATES.map((config) => (
          <TemplateCard
            key={config.type}
            config={config}
            row={rowMap.get(config.type)}
          />
        ))}
      </div>
    </>
  );
}
